import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ChatRoomListComponent } from '../chat-room-list/chat-room-list.component';
import { ChatConversationComponent } from '../chat-conversation/chat-conversation.component';
import { ChatService } from '../../../services/chat.service';
import { UserService } from '../../../services/user.service';
import { ChatRoomDTO } from '../../../models/chat-room-dto';

@Component({
  selector: 'app-chat-modal',
  standalone: true,
  imports: [ChatRoomListComponent, ChatConversationComponent],
  templateUrl: './chat-modal.component.html',
  styleUrl: './chat-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatModalComponent {
  activeModal = inject(NgbActiveModal);
  private chatService = inject(ChatService);
  private userService = inject(UserService);

  selectedRoom = signal<ChatRoomDTO | null>(null);
  private isFirstInstallationCheck = true;
  private hasAutoSelected = false;

  constructor() {
    // El chat depende completamente del filtro de instalación: al cambiar, se cierra la conversación
    // abierta y se vuelve a autoseleccionar la más reciente de la nueva instalación. La recarga de las
    // salas la hace ChatService por su cuenta (vacía su estado al cambiar la instalación).
    effect(() => {
      this.userService.selectedInstallationId();

      if (this.isFirstInstallationCheck) {
        this.isFirstInstallationCheck = false;
        return;
      }

      untracked(() => {
        this.selectedRoom.set(null);
        this.hasAutoSelected = false;
      });
    });

    // Cada recarga del estado único de salas (CHAT_REVISION.md B8) sustituye a lo que antes hacían
    // refreshSelectedRoom (GetRooms propio) y el output roomsLoaded de la lista.
    effect(() => {
      const rooms = this.chatService.rooms();
      if (!this.chatService.roomsLoaded()) return;

      untracked(() => this.onRoomsUpdated(rooms));
    });
  }

  /**
   * - Con una sala abierta: se sustituye por su versión fresca (nuevo título automático al cambiar
   *   miembros, IsActiveMember al ser expulsado...), o se cierra si ya no aparece (p. ej. su OR ha
   *   pasado a un estado que la oculta).
   * - Sin sala abierta, solo la primera vez que se listan salas (al abrir la modal o tras cambiar de
   *   instalación): se abre por defecto la de arriba del todo, la más reciente (el backend las ordena
   *   por LastMessageDate). En recargas posteriores no debe robarle el foco al usuario.
   */
  private onRoomsUpdated(rooms: ChatRoomDTO[]): void {
    const current = this.selectedRoom();
    if (current) {
      this.selectedRoom.set(rooms.find(r => r.IdChat === current.IdChat) ?? null);
      return;
    }

    if (this.hasAutoSelected) return;

    this.hasAutoSelected = true;
    if (rooms.length > 0) this.selectedRoom.set(rooms[0]);
  }

  onRoomSelected(room: ChatRoomDTO): void {
    this.selectedRoom.set(room);
  }

  onRoomLeft(): void {
    this.selectedRoom.set(null);
  }

  /** Flecha "atrás" del móvil (chat-conversation.component.scss): mismo efecto que onRoomLeft, pero
   *  sin salir de la sala — solo vuelve a mostrar la lista (ver has-selected-room en el HTML). */
  onBack(): void {
    this.selectedRoom.set(null);
  }

  close(): void {
    this.activeModal.close();
  }
}
